import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Send, Loader2, MapPin, Handshake, Users, Radio,
  ShieldCheck, Sparkles, Briefcase, Plus, KeyRound,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/* Map "icon" string from settings → lucide-react component. */
const ICONS = {
  MapPin, Handshake, Users, Radio, ShieldCheck, Sparkles, Briefcase,
};
function Icon({ name, ...rest }) {
  const C = ICONS[name] || Sparkles;
  return <C {...rest} />;
}

/**
 * /lootmaster — public Loot Master recruitment page.
 *
 * If `recruitment_open` is false on the public settings, this page
 * immediately redirects to / and pushes a toast with the closed message.
 * Application form posts to /api/lootmaster/apply.
 */
export default function LootMasterPage() {
  const [s, setS] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLM = !!user && (user.role === "lootmaster" || user.role === "admin");

  useEffect(() => {
    let cancelled = false;
    api.get("/lootmaster/settings").then(({ data }) => {
      if (cancelled) return;
      if (data && data.recruitment_open === false) {
        toast.message(
          data.closed_message ||
            "Recruitment is currently closed, but follow us on social media for updates!",
          { duration: 8000 }
        );
        navigate("/", { replace: true });
        return;
      }
      setS(data || {});
    }).catch(() => {
      if (!cancelled) setS({});
    });
    return () => { cancelled = true; };
  }, [navigate]);

  if (!s) {
    return (
      <div className="splash-root flex items-center justify-center" data-testid="lootmaster-page-loading">
        <div className="splash-mono text-sm opacity-60">Loading…</div>
      </div>
    );
  }

  return (
    <div className="splash-root" data-testid="lootmaster-page">
      <LMHero s={s} />
      {isLM && <LMPortalBanner />}
      <LMMission s={s} />
      <LMDoCards s={s} />
      <LMWhyCards s={s} />
      <LMFAQ s={s} />
      <LMApplicationForm s={s} />
      <LMFooter />
    </div>
  );
}

/* Banner shown only to logged-in lootmasters/admins, linking them straight
 * into the gated onboarding portal (/lootmaster/portal). */
function LMPortalBanner() {
  return (
    <section className="bg-[#0d100d] border-y border-[#d7a62a]/30 py-6" data-testid="lm-portal-banner">
      <div className="max-w-5xl mx-auto px-4 md:px-8 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <div className="splash-mono uppercase tracking-[0.22em] text-[10px] text-[#d7a62a]">
            You're already in
          </div>
          <div className="splash-display text-xl md:text-2xl uppercase mt-1">
            Jump to your Loot Master portal.
          </div>
        </div>
        <Link
          to="/lootmaster/portal"
          className="btn-loot-solid"
          data-testid="lm-portal-banner-cta"
        >
          <KeyRound size={14} /> Open portal <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}

/* ===================================================================== */

function LMHero({ s }) {
  return (
    <section className="splash-hero relative" data-testid="lm-hero">
      {s.page_hero_image_url && (
        <div className="splash-hero-bg" style={{ backgroundImage: `url(${s.page_hero_image_url})` }} aria-hidden="true" />
      )}
      <div className="splash-hero-overlay" aria-hidden="true" />
      <div className="splash-grain" aria-hidden="true" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-16 md:pb-24">
        <Link to="/" className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A] inline-flex items-center gap-2 hover:text-[#84d130]">
          <ArrowLeft size={12} /> Back to The Local Loot
        </Link>
        <div className="mt-8 splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
          {s.page_overline || "The Loot Master program"}
        </div>
        <h1 className="splash-display text-5xl md:text-7xl uppercase mt-3 leading-[0.95]">
          {(() => {
            const head = (s.page_headline || "Lead the hunt. Own the streets.").trim();
            const idx = head.indexOf(".");
            const first = idx >= 0 ? head.slice(0, idx + 1) : head;
            const rest = idx >= 0 ? head.slice(idx + 1).trim() : "";
            return (
              <>
                <span className="block headline-stamp">{first}</span>
                {rest && <span className="block headline-stamp--green mt-1 md:mt-2">{rest}</span>}
              </>
            );
          })()}
        </h1>
      </div>
    </section>
  );
}

function LMMission({ s }) {
  return (
    <section className="cream-section py-14 md:py-20" data-testid="lm-mission">
      <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 text-center">
        <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#4f8c19]">
          {s.mission_overline || "The mission"}
        </div>
        <p className="mt-4 splash-display text-2xl md:text-3xl uppercase leading-tight">
          {s.mission_body}
        </p>
      </div>
    </section>
  );
}

function LMDoCards({ s }) {
  const items = Array.isArray(s.do_items) ? s.do_items : [];
  return (
    <section className="bg-[#0d100d] border-t border-[#76B82A]/15 border-b border-[#76B82A]/15 py-14 md:py-20" data-testid="lm-do">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <h2 className="splash-display text-3xl md:text-5xl uppercase text-center">
          {s.do_overline || "What you'll do"}
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((it, i) => (
            <article key={i} className="private-card" data-testid={`lm-do-${i}`}>
              <span className="pc-icon"><Icon name={it.icon} size={20} /></span>
              <h3 className="splash-display text-xl md:text-2xl uppercase mt-3">{it.title}</h3>
              <p className="mt-2 text-[14px] text-[#f7f3e6]/75 leading-relaxed">{it.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function LMWhyCards({ s }) {
  const items = Array.isArray(s.why_items) ? s.why_items : [];
  return (
    <section className="cream-section py-14 md:py-20" data-testid="lm-why">
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <h2 className="splash-display text-3xl md:text-5xl uppercase text-center">
          {s.why_overline || "Why join the Loot Master network?"}
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5 md:gap-6">
          {items.map((it, i) => (
            <article
              key={i}
              className="bg-[#fffaef] border-2 border-[#0d100d] p-5 md:p-6 shadow-[6px_6px_0_#0d100d]"
              data-testid={`lm-why-${i}`}
            >
              <span className="step-icon">
                <Icon name={it.icon} size={22} />
              </span>
              <h3 className="splash-display text-xl md:text-2xl uppercase mt-3">{it.title}</h3>
              <p className="mt-2 text-[14px] text-[#0d100d]/75 leading-relaxed">{it.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function LMFAQ({ s }) {
  const items = Array.isArray(s.faq_items) ? s.faq_items : [];
  if (items.length === 0) return null;
  return (
    <section className="faq-section py-14 md:py-20" data-testid="lm-faq">
      <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center">
          <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#4f8c19]">
            {s.faq_overline || "Loot Master FAQs"}
          </div>
          <h2 className="splash-display text-3xl md:text-5xl uppercase mt-2">
            Quick answers.
          </h2>
        </div>
        <div className="mt-8">
          {items.map((it, i) => (
            <div key={i} className="faq-item" data-testid={`lm-faq-${i}`}>
              <details>
                <summary>
                  <span>{it.q}</span>
                  <Plus size={22} className="faq-toggle shrink-0" />
                </summary>
                <div className="faq-body">{it.a}</div>
              </details>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LMApplicationForm({ s }) {
  const [form, setForm] = useState({ name: "", email: "", location: "", why: "", background: "", social: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        location: form.location.trim(),
        why: form.why.trim(),
        background: form.background.trim(),
        social: form.social.trim() || undefined,
      };
      const { data } = await api.post("/lootmaster/apply", payload);
      setDone(data?.message || s.form_success_message || "Application received.");
      toast.success("Application sent");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" · ")
        : (typeof detail === "string" ? detail : "Couldn't submit. Try again in a moment.");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="bg-[#0d100d] py-20 md:py-28" data-testid="lm-form-success">
        <div className="max-w-2xl mx-auto px-4 md:px-8 text-center">
          <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A]">
            Sent
          </div>
          <h2 className="splash-display text-4xl md:text-6xl uppercase mt-3">
            <span className="headline-stamp--green">Stay ready.</span>
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#f7f3e6]/80 leading-relaxed">
            {done}
          </p>
          <Link to="/" className="btn-loot-solid mt-8" data-testid="lm-form-success-back">
            Back to The Local Loot <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#0d100d] py-16 md:py-24" data-testid="lm-form">
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        <div className="text-center">
          <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
            {s.form_overline || "Ready to lead?"}
          </div>
          <h2 className="splash-display text-3xl md:text-5xl uppercase mt-2">
            {s.form_headline || "Apply to be a Loot Master"}
          </h2>
          {s.form_subhead && (
            <p className="mt-3 text-[15px] text-[#f7f3e6]/75">{s.form_subhead}</p>
          )}
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="lm-form-el">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name *">
              <input
                required
                minLength={2}
                maxLength={120}
                value={form.name}
                onChange={onChange("name")}
                className="lm-input"
                data-testid="lm-form-name"
              />
            </Field>
            <Field label="Email (optional but recommended)">
              <input
                type="email"
                maxLength={200}
                value={form.email}
                onChange={onChange("email")}
                className="lm-input"
                data-testid="lm-form-email"
              />
            </Field>
            <Field label="Location (city, state) *" className="md:col-span-2">
              <input
                required
                minLength={2}
                maxLength={160}
                value={form.location}
                onChange={onChange("location")}
                className="lm-input"
                data-testid="lm-form-location"
                placeholder="Knoxville, TN"
              />
            </Field>
            <Field label="Current occupation / background *" className="md:col-span-2">
              <input
                required
                minLength={2}
                maxLength={600}
                value={form.background}
                onChange={onChange("background")}
                className="lm-input"
                data-testid="lm-form-background"
              />
            </Field>
            <Field label="Why do you want to be a Loot Master? *" hint="20+ characters" className="md:col-span-2">
              <textarea
                required
                minLength={20}
                maxLength={4000}
                rows={5}
                value={form.why}
                onChange={onChange("why")}
                className="lm-input"
                data-testid="lm-form-why"
              />
            </Field>
            <Field label="Link to your social media (optional)" className="md:col-span-2">
              <input
                maxLength={300}
                value={form.social}
                onChange={onChange("social")}
                className="lm-input"
                data-testid="lm-form-social"
                placeholder="@yourhandle or https://…"
              />
            </Field>
          </div>
          <div className="pt-2">
            <button
              type="submit"
              disabled={busy}
              className="btn-loot-solid w-full justify-center"
              data-testid="lm-form-submit"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {busy ? "Sending…" : (s.form_submit_label || "Submit application")}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, hint, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="flex items-baseline justify-between">
        <span className="splash-mono uppercase tracking-widest text-[10px] text-[#76B82A]">{label}</span>
        {hint && <span className="text-[10px] text-[#f7f3e6]/50">{hint}</span>}
      </div>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function LMFooter() {
  return (
    <footer className="splash-footer py-8 text-center">
      <div className="max-w-7xl mx-auto px-4 md:px-8 splash-mono text-[12px] text-[#f7f3e6]/55">
        © The Local Loot · Live Life. Do Good. Be Happy.
      </div>
    </footer>
  );
}
